import React, { useState } from "react";
import { formatDate, formatDateTime, formatCnpj } from '../../../utils/formatters';
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import {
  Box,
  Grid,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Link,
} from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";

const getInvoiceStateInfo = (state) => {
  const stateMap = {
    0: { label: intl.get('InvoiceState.Created').d('Created'), color: 'default' },
    1: { label: intl.get('InvoiceState.InProgress').d('In Progress'), color: 'info' },
    2: { label: intl.get('InvoiceState.Received').d('Received'), color: 'success' },
    3: { label: intl.get('InvoiceState.Cancelled').d('Cancelled'), color: 'error' },
    4: { label: intl.get('InvoiceState.Devolution').d('Devolution'), color: 'warning' },
    5: { label: intl.get('InvoiceState.Sent').d('Sent'), color: 'info' },
  };
  return stateMap[state] || { label: intl.get('InvoiceState.Unknown').d('Unknown'), color: 'default' };
};

const EntityCard = ({ icon, label, entity }) => (
  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {icon}
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
    </Box>
    {entity ? (
      <>
        <Typography variant="body1" fontWeight={600}>
          {entity.name}
        </Typography>
        {entity.cnpj && (
          <Typography variant="body2" color="text.secondary">
            CNPJ: {formatCnpj(entity.cnpj)}
          </Typography>
        )}
      </>
    ) : (
      <Typography variant="body2" color="text.secondary">-</Typography>
    )}
  </Paper>
);

const MAX_REASON_LENGTH = 100;

const CancellationReasonField = ({ reason }) => {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = reason.length > MAX_REASON_LENGTH;
  const displayText = !expanded && needsTruncation
    ? reason.slice(0, MAX_REASON_LENGTH) + '...'
    : reason;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600}>
        {intl.get("CancellationReason").d("Cancellation Reason")}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {displayText}
        {needsTruncation && (
          <Link
            component="button"
            variant="body2"
            sx={{ ml: 0.5, verticalAlign: 'baseline' }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded
              ? intl.get("Hide").d("Hide")
              : intl.get("More").d("More")}
          </Link>
        )}
      </Typography>
    </Box>
  );
};

export const ViewModeContent = ({ data, createdByLabel = undefined }) => {
  const invoice = data;

  if (!invoice) {
    return null;
  }

  const resolvedCreatedByLabel =
    createdByLabel || intl.get("CreatedBy").d("Created by");

  const stateInfo = getInvoiceStateInfo(invoice.invoice_state);
  const items = invoice.invoice_document_items || [];
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <Box>
      {/* Summary row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <Chip label={stateInfo.label} size="small" color={stateInfo.color} />
        {items.some(i => i.sent_at) && (
          <Typography variant="body2" color="text.secondary">
            {intl.get("SentAt").d("Sent At")}: <strong>{formatDateTime(items.find(i => i.sent_at)?.sent_at)}</strong>
          </Typography>
        )}
        {items.some(i => i.received_at) && (
          <Typography variant="body2" color="text.secondary">
            {intl.get("ReceivedAt").d("Received At")}: <strong>{formatDateTime(items.find(i => i.received_at)?.received_at)}</strong>
          </Typography>
        )}
        {invoice.invoice_state === 3 && invoice.updated_on && (
          <Typography variant="body2" color="text.secondary">
            {intl.get("CancelledAt").d("Cancelled At")}: <strong>{formatDateTime(invoice.updated_on)}</strong>
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {intl.get("Checkin.TotalQuantity").d("Total Quantity")}: <strong>{totalQuantity.toLocaleString('pt-BR')}</strong>
        </Typography>
        {invoice.created_by_display && (
          <Typography variant="body2" color="text.secondary">
            {resolvedCreatedByLabel}: <strong>{invoice.created_by_display}</strong>
          </Typography>
        )}
      </Box>

      {/* Origin / Destination cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6}>
          <EntityCard
            icon={<LocalShippingIcon sx={{ fontSize: 18, color: '#1565c0' }} />}
            label={intl.get('Origin').d('Origin')}
            entity={invoice.owner_entity}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <EntityCard
            icon={<StorefrontIcon sx={{ fontSize: 18, color: '#2e7d32' }} />}
            label={intl.get('Destination').d('Destination')}
            entity={invoice.destination_entity}
          />
        </Grid>
      </Grid>

      {/* Cancellation reason */}
      {invoice.invoice_state === 3 && invoice.cancellation_reason && (
        <CancellationReasonField reason={invoice.cancellation_reason} />
      )}

      <Divider sx={{ my: 2 }} />

      {/* Items header */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#1565c0' }}>
        {`${intl.get('InvoiceItems').d('Invoice Items')} (${items.length})`}
      </Typography>

      {/* Items table */}
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {intl.get('NoItemsFound').d('No items found')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                  {intl.get('Batch').d('Batch')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                  {intl.get('ProductDescription').d('Product Description')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                  {intl.get('Production').d('Production')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }}>
                  {intl.get('Expiration').d('Expiration')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#1565c0' }} align="center">
                  {intl.get('Quantity').d('Quantity')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={item.pk || index} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {item.batch?.number || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.batch?.part?.description || '-'}</TableCell>
                  <TableCell>{item.batch ? formatDate(item.batch.fabrication) : '-'}</TableCell>
                  <TableCell>{item.batch ? formatDate(item.batch.expiration) : '-'}</TableCell>
                  <TableCell align="center">{(item.quantity || 0).toLocaleString('pt-BR')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

ViewModeContent.propTypes = {
  data: PropTypes.object.isRequired,
  createdByLabel: PropTypes.string,
};
